import { useRef, useState, useEffect } from "react";
import { Button } from "../Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

interface FileUploadProps {
  label?: string;
  labelClassName?: string;
  placeholder?: string;
  className?: string;
  required?: boolean;
  preview: string | null;
  error?: string | null;
  helperText?: string;
  disabled?: boolean;
  canPreview?: boolean;
  showBorder?: boolean;
  borderClassName?: string;
  previewHeight?: number;
  blackTheme?: boolean;
  onSelect?: (file: File | null) => void;
  onRemove?: () => void;
}

export function FileUpload({
  label,
  labelClassName,
  placeholder = "",
  className,
  required,
  disabled,
  preview,
  error,
  helperText,
  canPreview = false,
  showBorder = true,
  borderClassName,
  previewHeight = 150,
  blackTheme = true,
  onSelect,
  onRemove,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    if (isOpen) {
      window.addEventListener("keydown", handler);
    }

    return () => window.removeEventListener("keydown", handler);
  }, [isOpen]);

  const remove = () => {
    onRemove?.();
    if (inputRef.current) inputRef.current.value = "";
  };

  const openPreview = () => {
    if (canPreview) setIsOpen(true);
  };

  return (
    <div className={className}>
      {label && (
        <label
          className={`text-sm block mb-2 ml-1 ${labelClassName} ${blackTheme ? "text-white/70" : ""
            }`}
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        {!preview ? (
          <label
            htmlFor={label}
            className={`
              cursor-pointer block
              ${blackTheme
                ? "bg-black hover:bg-white/10 p-5 rounded-xl"
                : "bg-[#F6F6F6] p-3 rounded-[4px]"
              }
              ${disabled ? "cursor-not-allowed opacity-50" : ""}
              ${borderClassName
                ? borderClassName
                : showBorder
                  ? "border border-dashed border-white/20"
                  : ""
              }
            `}
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <img
                  src={
                    blackTheme
                      ? "/icons/ic-upload.svg"
                      : "/icons/ic-upload-black.svg"
                  }
                  className="size-5 opacity-80"
                  alt="Upload"
                />
                <span
                  className={`${blackTheme
                      ? "text-white"
                      : "text-[#3C3C3C] font-semibold text-sm"
                    }`}
                >
                  {placeholder || label}
                </span>
              </div>
              <p
                className={`${blackTheme
                    ? "text-white/50 text-xs"
                    : "text-[#3C3C3C] font-medium text-sm text-left"
                  }`}
              >
                {helperText || "Click to upload"}
              </p>
            </div>
          </label>
        ) : (
          <div
            style={{
              minHeight: previewHeight,
              maxHeight: previewHeight,
              height: previewHeight,
            }}
            className={`
              relative
              rounded-md
              bg-white/5
              flex items-center justify-center
              overflow-hidden
              ${borderClassName
                ? borderClassName
                : showBorder
                  ? "border border-dashed border-white/20"
                  : ""
              }
            `}
          >
            <img
              src={preview}
              alt={label}
              onClick={openPreview}
              className={`
                max-h-full
                max-w-full
                object-contain
                ${canPreview ? "cursor-pointer hover:opacity-80" : ""}
              `}
            />
          </div>
        )}

        {preview && !disabled && (
          <Button
            type="button"
            onClick={remove}
            className="absolute top-3 right-3 w-7 h-7 bg-black/70 rounded-full text-white hover:bg-black flex items-center justify-center"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        )}

        <input
          ref={inputRef}
          id={label}
          type="file"
          hidden
          accept="image/png,image/jpeg"
          onChange={(e) => onSelect?.(e.target.files?.[0] ?? null)}
          disabled={disabled}
        />
      </div>

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      {canPreview && (
        <div
          className={`
            fixed inset-0 z-50 flex items-center justify-center bg-black/80
            transition-opacity duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
            ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
          `}
          onClick={() => setIsOpen(false)}
        >
          {preview && (
            <img
              src={preview}
              alt={label}
              onClick={(e) => e.stopPropagation()}
              className={`
                max-w-[90vw] max-h-[90vh]
                object-contain
                transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
                ${isOpen ? "scale-100 opacity-100" : "scale-95 opacity-0"}
              `}
            />
          )}
        </div>
      )}
    </div>
  );
}